<script setup>
import { computed, toRefs } from "vue";
import SteppedProcess from "@/components/SteppedProcess/SteppedProcess.vue";
import ArrowLeft from "@/components/shared/icons/ArrowLeft.vue";
import { useAuthorization } from "@/composables/authorization.js";
import { useApplicationSteps } from "../composables/useApplicationSteps";
import { buildOrderQueryParams } from "../utils/utils";

import AccountTypeNotAllowed from "./AccountTypeNotAllowed.vue";
import SessionBanner from "./SessionBanner.vue";

const props = defineProps({
  // see useApplication.js for application definition
  application: {
    type: Object,
    default: () => {},
    required: true,
  },
  step: {
    type: String,
    default: "",
    requird: false,
  },
  stage: {
    type: String,
    default: "apply",
    required: true,
  },
});

const { application } = toRefs(props);

const { isStudent } = useAuthorization();

const { currentStep, setCurrentStep, activeSteps } = useApplicationSteps(application);

const { applicationId, session } = application.value;

const unsavedOrder = computed(() => application.value?.unsavedOrder?.value);
const unsavedLearnerTypeId = computed(() => application.value?.unsavedLearnerTypeId?.value);

const linkBackToConfigurator = computed(() => {
  if (!isPersonalized.value) {
    return "";
  }

  const order = unsavedOrder.value;

  if (applicationId.value) {
    return {
      name: "modify-application",
      params: {
        applicationId: applicationId.value,
        stage: "personalize",
      },
      query: buildOrderQueryParams(order),
    };
  }

  return {
    name: "new-application",
    params: {
      programPageId: application.value.programPageId.value,
      sessionId: application.value.sessionId.value,
      stage: "personalize",
    },
    query: { ...buildOrderQueryParams(order), learnerType: unsavedLearnerTypeId.value },
  };
});

const isPersonalized = computed(() => session.value?.is_personalized);
</script>

<template>
  <div v-if="isStudent" class="w-full justify-center min-h-screen bg-white md:mb-24">    
    <div class="flex w-full max-w-[650px] mx-auto justify-center bg-white">
      <div class="flex flex-col w-full px-4 gap-4 md:px-0 md:gap-7">
        <router-link v-if="isPersonalized" :to="linkBackToConfigurator" class="flex">
          <ArrowLeft :custom-class="`w-4 h-4 mt-1 ${secondaryColorClass}`" />
          <span :class="`font-bold text-base ${secondaryColorClass}`">
            Back to Selections
          </span>
        </router-link>
        <SessionBanner :session="session" />
        <stepped-process
          class="flex flex-col bg-white md:gap-7"
          :steps="activeSteps"
          :current-step="currentStep"
          @set-current-step="setCurrentStep"
        />
      </div>
    </div>
  </div>
  <account-type-not-allowed v-else-if="session" />
</template>
